/** The status vocabulary — every label and colour a state gets in the console,
 *  in one table (plan 1-3). Screens never spell a state out themselves: they
 *  call `statusOf(domain, key)` and render a StatusPill. */

export type Tone =
  | 'neutral'
  | 'review'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'deadline'
  | 'web'

export interface StatusDef {
  label: string
  tone: Tone
  /** The dot blinks: something is under way right now. */
  pulse?: boolean
  /** Shown faded: the item is out of play. */
  dim?: boolean
}

/** The last sync pass (job_runs `sync`, newest row and its details; see
 *  `syncKey` in lib/spend.ts). `partial` is a run that finished ok with an
 *  AdMob error in its details (a Google Ads refusal is the spend domain's);
 *  `stale` is the last ok pass more
 *  than three hours ago with nothing running (two hourly runs missed). */
const SYNC: Record<string, StatusDef> = {
  never: { label: '실행 전', tone: 'neutral' },
  running: { label: '실행 중', tone: 'publishing', pulse: true },
  ok: { label: '정상', tone: 'published' },
  partial: { label: '일부 실패', tone: 'publishing' },
  error: { label: '실패', tone: 'failed' },
  stale: { label: '오래됨', tone: 'publishing' },
}

/** Where ad spend stands (`spendState` in lib/spend.ts). It decides whether
 *  the spend, profit and ROAS cells show numbers or a dash. */
const SPEND: Record<string, StatusDef> = {
  unlinked: { label: 'Google Ads 미연결', tone: 'neutral' },
  blocked: { label: 'Ads 접근 거부', tone: 'failed' },
  error: { label: 'Ads 오류', tone: 'failed' },
  nodata: { label: '캠페인 없음', tone: 'neutral' },
  ok: { label: '정상', tone: 'published' },
  // Settings › Google Ads switched off (GoogleStatus.ads_enabled).
  off: { label: '꺼 둠', tone: 'neutral' },
}

/** The AdMob side of the connection (`admobKey` in lib/spend.ts). */
const ADMOB: Record<string, StatusDef> = {
  unset: { label: 'AdMob 미설정', tone: 'neutral' },
  error: { label: 'AdMob 오류', tone: 'failed' },
  ok: { label: '정상', tone: 'published' },
  // Settings › AdMob switched off (GoogleStatus.admob_enabled).
  off: { label: '꺼 둠', tone: 'neutral' },
}

/** The Google account (GET /api/google/status `connected`). */
const GOOGLE: Record<string, StatusDef> = {
  connected: { label: '연결됨', tone: 'published' },
  offline: { label: '미연결', tone: 'neutral' },
}

/** An app's, a day's or a total's profit (`profitKey`). The numbers stay
 *  in the text colour; this pill is what says 적자. */
const PROFIT: Record<string, StatusDef> = {
  surplus: { label: '흑자', tone: 'published' },
  deficit: { label: '적자', tone: 'failed' },
  idle: { label: '수익 없음', tone: 'neutral', dim: true },
}

/** ROAS band, only when there was spend (`roasKey`). */
const ROAS: Record<string, StatusDef> = {
  good: { label: '좋음', tone: 'published' },
  even: { label: '손익분기', tone: 'neutral' },
  under: { label: '미달', tone: 'failed' },
}

/** A Google Ads campaign's status. */
const CAMPAIGN: Record<string, StatusDef> = {
  ENABLED: { label: '운영 중', tone: 'published' },
  PAUSED: { label: '일시정지', tone: 'neutral' },
  REMOVED: { label: '삭제됨', tone: 'neutral', dim: true },
  UNKNOWN: { label: '미확인', tone: 'neutral' },
}

/** A budget pool. CAPPED = the members spent the total, ENDED = the end date
 *  passed. Either way the sync paused them, and they stay paused until the
 *  operator fixes the pool and turns them on. */
const POOL: Record<string, StatusDef> = {
  ACTIVE: { label: '집행 중', tone: 'published' },
  CAPPED: { label: '총액 도달', tone: 'deadline' },
  ENDED: { label: '기간 종료', tone: 'neutral' },
}

/** Who made a write to the ad platform (write_log `actor`). */
const WRITE_ACTOR: Record<string, StatusDef> = {
  console: { label: '콘솔', tone: 'neutral' },
  sync: { label: '동기화', tone: 'neutral' },
}

/** Which ad network a campaign or a write belongs to (`network`). */
const NETWORK: Record<string, StatusDef> = {
  google: { label: 'Google', tone: 'neutral' },
  apple: { label: 'Apple', tone: 'neutral' },
}

/** What the write was (write_log `action`). */
const WRITE_ACTION: Record<string, StatusDef> = {
  campaign_create: { label: '캠페인 생성', tone: 'neutral' },
  campaign_status: { label: '상태 변경', tone: 'neutral' },
  campaign_budget: { label: '예산 변경', tone: 'neutral' },
  spend_cap_pause: { label: '상한 정지', tone: 'neutral' },
  pool_pause: { label: '묶음 정지', tone: 'neutral' },
}

/** Whether Google took the write (write_log `ok`: `ok` / `failed`). */
const WRITE_RESULT: Record<string, StatusDef> = {
  ok: { label: '성공', tone: 'published' },
  failed: { label: '실패', tone: 'failed' },
}

/** A campaign's type (the key stays in the title). */
const CAMPAIGN_TYPE: Record<string, StatusDef> = {
  APP_INSTALLS: { label: '앱 설치', tone: 'neutral' },
  APP_ENGAGEMENT: { label: '앱 참여', tone: 'neutral' },
}

/** What the autopilot recommends (GET /api/autopilot `action`). */
const AUTOPILOT_ACTION: Record<string, StatusDef> = {
  pause: { label: '일시정지 권고', tone: 'publishing' },
  scale_budget: { label: '예산 증액 권고', tone: 'scheduled' },
}

/** Why it recommends it (`reason`). */
const AUTOPILOT_REASON: Record<string, StatusDef> = {
  no_conversions: { label: '7일 전환 0', tone: 'neutral' },
  low_roas: { label: 'ROAS 미달', tone: 'neutral' },
  high_roas: { label: 'ROAS 초과', tone: 'neutral' },
}

/** An alert's severity. The server raises warning and critical; info is
 *  listed so an older row still reads. */
const ALERT_SEVERITY: Record<string, StatusDef> = {
  critical: { label: '심각', tone: 'failed' },
  warning: { label: '주의', tone: 'publishing' },
  info: { label: '안내', tone: 'scheduled' },
}

/** An alert's rule (the title itself is the server's Korean). */
const ALERT_TYPE: Record<string, StatusDef> = {
  spend_spike: { label: '지출 급증', tone: 'neutral' },
  revenue_drop: { label: '수익 급락', tone: 'neutral' },
  deficit_app: { label: '적자 앱', tone: 'neutral' },
  sync_error: { label: '동기화 오류', tone: 'neutral' },
  spend_cap: { label: '지출 상한', tone: 'neutral' },
  budget_pool: { label: '예산 묶음', tone: 'neutral' },
}

const ALERT_READ: Record<string, StatusDef> = {
  unread: { label: '안 읽음', tone: 'scheduled' },
  read: { label: '읽음', tone: 'neutral', dim: true },
}

/** A day on the calendar or in the day report. `partial` is today, still
 *  being revised by AdMob — no blinking: it is not work under way here. */
const DAY: Record<string, StatusDef> = {
  partial: { label: '집계 중', tone: 'publishing' },
  deficit: { label: '적자', tone: 'failed' },
}

/** A job_runs row. */
const JOB: Record<string, StatusDef> = {
  running: { label: '실행 중', tone: 'publishing', pulse: true },
  ok: { label: '성공', tone: 'published' },
  error: { label: '실패', tone: 'failed' },
  skipped: { label: '건너뜀', tone: 'neutral' },
  cancelled: { label: '취소', tone: 'neutral', dim: true },
}

/** An app's platform (AdMob `platform`). */
const PLATFORM: Record<string, StatusDef> = {
  ANDROID: { label: 'Android', tone: 'neutral' },
  IOS: { label: 'iOS', tone: 'neutral' },
  unknown: { label: '미상', tone: 'neutral' },
}

export const STATUS = {
  sync: SYNC,
  spend: SPEND,
  admob: ADMOB,
  google: GOOGLE,
  profit: PROFIT,
  roas: ROAS,
  campaign: CAMPAIGN,
  campaignType: CAMPAIGN_TYPE,
  pool: POOL,
  network: NETWORK,
  writeActor: WRITE_ACTOR,
  writeAction: WRITE_ACTION,
  writeResult: WRITE_RESULT,
  autopilotAction: AUTOPILOT_ACTION,
  autopilotReason: AUTOPILOT_REASON,
  alertSeverity: ALERT_SEVERITY,
  alertType: ALERT_TYPE,
  alertRead: ALERT_READ,
  day: DAY,
  job: JOB,
  platform: PLATFORM,
} as const

export type StatusDomain = keyof typeof STATUS

/** The label and tone for a state. An unknown key comes back as itself,
 *  neutral, so a new server state shows up instead of vanishing. */
export function statusOf(domain: StatusDomain, key: string): StatusDef {
  return (STATUS[domain] as Record<string, StatusDef>)[key] ?? { label: key, tone: 'neutral' }
}

/** Profit's key: above zero 흑자, below 적자, nothing earned and nothing
 *  spent 수익 없음. A cent either way counts. */
export function profitKey(m: { revenue: number; cost: number; profit: number }): 'surplus' | 'deficit' | 'idle' {
  if (m.revenue <= 0 && m.cost <= 0) return 'idle'
  return m.profit < 0 ? 'deficit' : 'surplus'
}

/** ROAS's band, or null when nothing was spent (there is no ratio to judge). */
export function roasKey(roas: number, cost: number): 'good' | 'even' | 'under' | null {
  if (!(cost > 0)) return null
  if (roas >= 1.5) return 'good'
  if (roas >= 1) return 'even'
  return 'under'
}

/** A platform as the vocabulary knows it; anything else is 미상. */
export function platformKey(p: string | null | undefined): 'ANDROID' | 'IOS' | 'unknown' {
  const v = (p ?? '').toUpperCase()
  return v === 'ANDROID' || v === 'IOS' ? v : 'unknown'
}

/** The names the job keys get on screen. A key not listed shows as itself. */
export const JOB_LABEL: Record<string, string> = {
  sync: '동기화',
  session_purge: '세션 정리',
}

export function jobLabel(job: string): string {
  return JOB_LABEL[job] ?? job
}

/** Class names per tone. Kept literal so Tailwind sees every one. */
export const TONE_CLASS: Record<Tone, { text: string; soft: string; dot: string; border: string }> = {
  neutral: {
    text: 'text-status-neutral',
    soft: 'bg-status-neutral-soft',
    dot: 'bg-status-neutral',
    border: 'border-status-neutral/30',
  },
  review: {
    text: 'text-status-review',
    soft: 'bg-status-review-soft',
    dot: 'bg-status-review',
    border: 'border-status-review/30',
  },
  scheduled: {
    text: 'text-status-scheduled',
    soft: 'bg-status-scheduled-soft',
    dot: 'bg-status-scheduled',
    border: 'border-status-scheduled/30',
  },
  publishing: {
    text: 'text-status-publishing',
    soft: 'bg-status-publishing-soft',
    dot: 'bg-status-publishing',
    border: 'border-status-publishing/30',
  },
  published: {
    text: 'text-status-published',
    soft: 'bg-status-published-soft',
    dot: 'bg-status-published',
    border: 'border-status-published/30',
  },
  failed: {
    text: 'text-status-failed',
    soft: 'bg-status-failed-soft',
    dot: 'bg-status-failed',
    border: 'border-status-failed/30',
  },
  deadline: {
    text: 'text-status-deadline',
    soft: 'bg-status-deadline-soft',
    dot: 'bg-status-deadline',
    border: 'border-status-deadline/30',
  },
  web: {
    text: 'text-status-web',
    soft: 'bg-status-web-soft',
    dot: 'bg-status-web',
    border: 'border-status-web/30',
  },
}
